import type { PunchType, Target } from '../recog/events';

/**
 * AI archetypes.
 *
 * The design goal is that a player should be able to say "oh, this is a
 * pressure fighter" within twenty seconds and then *change what they do about
 * it*. So archetypes differ along axes that have direct counterplay, not along
 * cosmetic stat lines:
 *
 *   preferredGap   -> counter by fighting at the range they hate
 *   feintRate      -> counter by reacting to the punch, not the twitch
 *   guardStyle     -> counter by choosing punches their guard does not cover
 *   tellMs         -> the learnable "read"; every fighter leaks one thing
 *   recoveryMs     -> the counter window they hand you
 *
 * `tell` is the most important field in this file. A boxer with no readable
 * pattern is not "hard", it is noise; the fun of sparring is pattern discovery.
 */

export interface ComboScript {
  /** Punch numbers in boxing notation order. */
  seq: Array<{ type: PunchType; lead: boolean; target: Target }>;
  weight: number;
  /** ms between punches in the run. */
  tempoMs: number;
}

export type GuardStyle = 'high-guard' | 'philly-shell' | 'peekaboo' | 'long-guard';

export interface Personality {
  id: string;
  name: string;
  blurb: string;
  /** What the player should learn to look for. Shown after the first round. */
  tell: string;

  /** Range they try to hold, in shoulder widths. */
  preferredGap: number;
  /** How hard they fight to restore it, 0..1. */
  rangeDiscipline: number;

  /** Baseline willingness to initiate, 0..1. Modulated by momentum and fatigue. */
  aggression: number;
  /** Willingness to trade — take a shot to land one, 0..1. */
  riskTolerance: number;
  /** Probability a committed attack is a feint. */
  feintRate: number;
  /** Fraction of defensive responses that are head movement rather than blocking. */
  evasionBias: number;
  guardStyle: GuardStyle;

  /** Extra wind-up on top of the punch's base, ms. The archetype's signature tell. */
  tellMs: number;
  /** How long the hands stay out after a punch. Your counter window. */
  recoveryMs: number;

  /** Stamina drain multiplier. Sluggers and swarmers pay for their style. */
  fatigueRate: number;
  /** How much fatigue degrades their guard vs their output, 0 = output, 1 = guard. */
  fatigueGuardBias: number;

  combos: ComboScript[];
  /** Weighted single-punch preferences when not running a scripted combo. */
  singles: Array<{ type: PunchType; lead: boolean; target: Target; weight: number }>;
}

const P = (type: PunchType, lead: boolean, target: Target = 'head') => ({ type, lead, target });

export const PERSONALITIES: Personality[] = [
  {
    id: 'metronome',
    name: 'The Metronome',
    blurb: 'Long-range out-boxer. Lives behind the jab, resets constantly.',
    tell: 'Always steps out immediately after the second jab — that reset is your window.',
    preferredGap: 1.5,
    rangeDiscipline: 0.85,
    aggression: 0.35,
    riskTolerance: 0.2,
    feintRate: 0.18,
    evasionBias: 0.35,
    guardStyle: 'long-guard',
    tellMs: 55,
    recoveryMs: 200,
    fatigueRate: 0.7,
    fatigueGuardBias: 0.4,
    combos: [
      { seq: [P('jab', true), P('jab', true)], weight: 3, tempoMs: 260 },
      { seq: [P('jab', true), P('cross', false)], weight: 2, tempoMs: 220 },
      { seq: [P('jab', true), P('jab', true), P('cross', false)], weight: 1, tempoMs: 240 },
    ],
    singles: [
      { ...P('jab', true), weight: 6 },
      { ...P('cross', false), weight: 2 },
      { ...P('jab', true, 'body'), weight: 1 },
    ],
  },
  {
    id: 'pressure',
    name: 'Rolling Thunder',
    blurb: 'Swarmer. Closes distance and throws in bursts. Punishes passivity.',
    tell: 'Rolls his head left before every hook — the roll comes first, the shot follows.',
    preferredGap: 0.95,
    rangeDiscipline: 0.7,
    aggression: 0.85,
    riskTolerance: 0.7,
    feintRate: 0.1,
    evasionBias: 0.65,
    guardStyle: 'peekaboo',
    tellMs: 35,
    recoveryMs: 150,
    fatigueRate: 1.45,
    fatigueGuardBias: 0.25,
    combos: [
      { seq: [P('hook', true, 'body'), P('hook', true)], weight: 3, tempoMs: 190 },
      { seq: [P('cross', false, 'body'), P('hook', true), P('cross', false)], weight: 3, tempoMs: 175 },
      { seq: [P('uppercut', true), P('hook', false), P('hook', true)], weight: 2, tempoMs: 165 },
    ],
    singles: [
      { ...P('hook', true), weight: 4 },
      { ...P('hook', false), weight: 2 },
      { ...P('uppercut', true), weight: 2 },
      { ...P('cross', false, 'body'), weight: 3 },
    ],
  },
  {
    id: 'counter',
    name: 'The Trap',
    blurb: 'Counter-puncher. Does almost nothing until you commit, then makes you pay.',
    tell: 'Baits with a lowered lead hand. If the lead drops, the counter cross is already loaded.',
    preferredGap: 1.35,
    rangeDiscipline: 0.75,
    // Low, but not so low that it never initiates. A counter-puncher that only
    // ever reacts becomes a heavy bag once the player stops giving it openings.
    aggression: 0.38,
    riskTolerance: 0.35,
    feintRate: 0.35,
    evasionBias: 0.75,
    guardStyle: 'philly-shell',
    tellMs: 25,
    recoveryMs: 165,
    fatigueRate: 0.85,
    fatigueGuardBias: 0.6,
    combos: [
      { seq: [P('cross', false), P('hook', true)], weight: 3, tempoMs: 180 },
      { seq: [P('cross', false), P('hook', true), P('cross', false)], weight: 2, tempoMs: 190 },
    ],
    singles: [
      { ...P('cross', false), weight: 5 },
      { ...P('hook', true), weight: 3 },
      { ...P('uppercut', false), weight: 2 },
    ],
  },
  {
    id: 'slugger',
    name: 'Sledge',
    blurb: 'Heavy hands, slow feet. Every shot is looking for the finish.',
    tell: 'Drops the rear shoulder a full beat before the overhand. You get a long look at it.',
    preferredGap: 1.15,
    rangeDiscipline: 0.5,
    aggression: 0.6,
    riskTolerance: 0.85,
    feintRate: 0.08,
    evasionBias: 0.2,
    guardStyle: 'high-guard',
    tellMs: 120,
    recoveryMs: 320,
    fatigueRate: 1.6,
    fatigueGuardBias: 0.7,
    combos: [
      { seq: [P('overhand', false), P('hook', true)], weight: 3, tempoMs: 260 },
      { seq: [P('jab', true), P('overhand', false)], weight: 2, tempoMs: 300 },
    ],
    singles: [
      { ...P('overhand', false), weight: 4 },
      { ...P('hook', true), weight: 3 },
      { ...P('uppercut', false), weight: 2 },
      { ...P('jab', true), weight: 2 },
    ],
  },
  {
    id: 'trickster',
    name: 'Quicksilver',
    blurb: 'Rhythm-breaker. Feints constantly and never throws the same beat twice.',
    tell: 'The real punch always arrives off-beat. If it looks rhythmic, it is a feint.',
    preferredGap: 1.25,
    rangeDiscipline: 0.6,
    aggression: 0.55,
    riskTolerance: 0.5,
    feintRate: 0.5,
    evasionBias: 0.6,
    guardStyle: 'philly-shell',
    tellMs: 15,
    recoveryMs: 175,
    fatigueRate: 1.0,
    fatigueGuardBias: 0.5,
    combos: [
      { seq: [P('jab', true), P('uppercut', false), P('hook', true)], weight: 3, tempoMs: 155 },
      { seq: [P('hook', true, 'body'), P('cross', false)], weight: 2, tempoMs: 200 },
      { seq: [P('jab', true), P('jab', true, 'body'), P('cross', false)], weight: 2, tempoMs: 185 },
    ],
    singles: [
      { ...P('jab', true), weight: 3 },
      { ...P('hook', true), weight: 3 },
      { ...P('uppercut', false), weight: 2 },
      { ...P('cross', false), weight: 3 },
    ],
  },
  {
    id: 'veteran',
    name: 'The Professor',
    blurb: 'Balanced, disciplined, adapts to what you show him. The exam.',
    tell: 'He copies your last successful combination back at you within two exchanges.',
    preferredGap: 1.3,
    rangeDiscipline: 0.9,
    aggression: 0.5,
    riskTolerance: 0.45,
    feintRate: 0.28,
    evasionBias: 0.55,
    guardStyle: 'high-guard',
    tellMs: 30,
    recoveryMs: 170,
    fatigueRate: 0.9,
    fatigueGuardBias: 0.5,
    combos: [
      { seq: [P('jab', true), P('cross', false), P('hook', true)], weight: 3, tempoMs: 195 },
      { seq: [P('jab', true), P('cross', false, 'body'), P('hook', true)], weight: 2, tempoMs: 200 },
      { seq: [P('hook', true), P('cross', false), P('hook', true)], weight: 2, tempoMs: 185 },
    ],
    singles: [
      { ...P('jab', true), weight: 4 },
      { ...P('cross', false), weight: 3 },
      { ...P('hook', true), weight: 3 },
      { ...P('uppercut', false), weight: 1 },
    ],
  },
];

export function personalityById(id: string): Personality {
  return PERSONALITIES.find((p) => p.id === id) ?? PERSONALITIES[0];
}
